import { useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, UserRound, Layers } from "lucide-react";
import api from "@/lib/api";
import { cycleText } from "@/components/Cycle";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

export const sessionTag = (si) => {
  if (!si) return "";
  const n = si.session_no ? `Session ${si.session_no}${si.sessions_total > 0 ? ` of ${si.sessions_total}` : ""}` : `${si.sessions_completed}${si.sessions_total > 0 ? `/${si.sessions_total}` : ""} done`;
  return `${n} · Cycle ${si.cycle || 1}`;
};

export function IssueBadges({ issues = [], testid }) {
  if (!issues.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid={testid}>
      {issues.map((i) => (
        <span key={i.kind} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${i.kind === "missed" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
          <AlertTriangle size={10} /> {i.text}
        </span>
      ))}
    </span>
  );
}

function Chip({ onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="rounded-full border border-teal-200 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-teal-800 hover:bg-teal-50 transition-colors">
      {children}
    </button>
  );
}

export function useShiftCheck({ clinicianId, date, start, end, patientId, sid }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!clinicianId || !date) { setData(null); return undefined; }
    const t = setTimeout(() => {
      api.get("/ops/roster/check", { params: { clinician_id: clinicianId, date, start, end, patient_id: patientId || "", sid: sid || "" } })
        .then((r) => setData(r.data)).catch(() => setData(null));
    }, 300);
    return () => clearTimeout(t);
  }, [clinicianId, date, start, end, patientId, sid]);
  return data;
}

export default function ShiftCheck({ data, onPickDate, onPickClinician, prefix = "shift-check" }) {
  if (!data) return null;
  const si = data.session_info;
  const flagged = data.issues?.length > 0;
  return (
    <div className={`mt-3 rounded-xl border px-3.5 py-3 space-y-2.5 ${flagged ? "border-rose-200 bg-rose-50/40" : "border-sky-100 bg-sky-50/50"}`} data-testid={prefix}>
      {si && (
        <div className="flex items-center gap-1.5 text-[12.5px] text-slate-800" data-testid={`${prefix}-session`}>
          <Layers size={13} className="text-sky-700" /><b>{sessionTag(si)}</b>
          <span className="text-slate-500">· {si.sessions_completed} logged so far{si.cycle > 1 ? ` · ${cycleText(si.cycle)}` : ""}</span>
        </div>
      )}
      <IssueBadges issues={data.issues} testid={`${prefix}-issues`} />
      {(flagged || onPickClinician) && data.dates?.length > 0 && onPickDate && (
        <div>
          <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mb-1"><CalendarClock size={12} /> {flagged ? "Suggested dates" : "Other free dates"} for this therapist</div>
          <div className="flex flex-wrap gap-1.5">{data.dates.map((d) => <Chip key={d.date} onClick={() => onPickDate(d)} testid={`${prefix}-date-${d.date}`}>{fmt(d.date)} · {d.start}</Chip>)}</div>
        </div>
      )}
      {onPickClinician && data.clinicians?.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mb-1"><UserRound size={12} /> Free therapists on {fmt(data.clinicians[0].date)}</div>
          <div className="flex flex-wrap gap-1.5">{data.clinicians.map((c) => <Chip key={c.id} onClick={() => onPickClinician(c)} testid={`${prefix}-clin-${c.id}`}>{c.name} · {c.open_cases} open</Chip>)}</div>
        </div>
      )}
    </div>
  );
}
